import * as React from 'react';
import { Box, FormHelperText, TextField, Autocomplete as MuiAutocomplete, createFilterOptions, autocompleteClasses, useForkRef, } from '@mui/material';
import omit from 'lodash-es/omit';
import clsx from 'clsx';
import { IconError, IconChecked, IconChevronSmall } from '../../internal/icons';
import getStyle, { autocompleteClassesEnum, optionClasses } from './style';
import { getOptionValue, getCheckboxValue, generateUniqueKey, isEqualOptions, } from './utils';
import { Checkbox } from '../../Checkbox';
import { Tag } from '../../Tag';
import ClearButton from './ClearButton';
import { extractAriaProps } from '../../Shared/props';
import { useTranslation } from '../../hooks/useTranslation';
import OverflowTooltip from '../../Shared/OverflowTooltip';
import { useMixedValue } from '../../hooks';
const SELECT_ALL = '_SELECT_ALL';
function InternalAutocomplete(props, ref) {
    const { t } = useTranslation('Autocomplete');
    // Props validation: Check for potentially problematic combinations
    React.useEffect(() => {
        const { creatable, multiple, checkboxSelection, renderOption, showDuplicateLabels } = props;
        // istanbul ignore next
        if (process.env.NODE_ENV !== 'production') {
            // High-risk combination: creatable + multiple + checkboxSelection
            if (creatable && multiple && checkboxSelection) {
                console.warn('[Autocomplete] Using creatable + multiple + checkboxSelection together may cause state synchronization issues. Consider using simpler combinations for better reliability.');
            }
            // Medium-risk combination: custom renderOption + showDuplicateLabels=false
            if (renderOption && showDuplicateLabels === false) {
                console.warn('[Autocomplete] Using custom renderOption with showDuplicateLabels=false may cause rendering issues. Ensure your custom render function handles filtered options correctly.');
            }
            // Type safety warning for valueName
            if (checkboxSelection && typeof checkboxSelection === 'object' && checkboxSelection.valueName) {
                const { valueName } = checkboxSelection;
                if (typeof valueName !== 'string' || valueName.trim() === '') {
                    console.warn('[Autocomplete] checkboxSelection.valueName should be a non-empty string representing a valid object property name.');
                }
            }
        }
    }, [props]);
    const internalProps = omit(props, ['style', 'disableClearable']);
    const { autoFocus, checkboxSelection, componentsProps = {}, creatable = false, className, disabled, readOnly, allowClear, status, freeSolo, getCreateLabel = (value) => t('addLabel', { value }), getOptionLabel = (option) => option?.label ?? option, getLimitTagsText = (more) => t('limitTags', { more }), helperText, inputRef, inputProps, loadingText = t('loading'), multiple = false, noOptionsText = t('noOptions'), onChange, value: valueProp, optionFilterName = 'children', options = [], sx = [], placeholder, size = 'medium', getOptionDisabled, renderGroup: renderGroupProp, renderOption: userRenderOption, longOptionsDisplay, showDuplicateLabels = true, clearButtonProps = {}, ...restProps } = internalProps;
    const [ariaProps, rest] = extractAriaProps(restProps);
    const { filterOptions, defaultValue } = rest;
    const error = status === 'error';
    const containerRef = React.useRef();
    const [containerWidth, setContainerWidth] = React.useState();
    function handleOpen(e) {
        rest.onOpen?.(e);
        setContainerWidth(containerRef.current?.clientWidth);
    }
    const paperRef = React.useRef();
    const canCreateRef = React.useRef(false);
    const resetCreatablePaperStyle = (canCreate) => {
        canCreateRef.current = canCreate;
        if (!paperRef.current) {
            return;
        }
        const paperElem = paperRef.current;
        // istanbul ignore else
        if (canCreate) {
            // istanbul ignore else
            if (!paperElem.classList.contains(autocompleteClassesEnum.canCreate)) {
                paperElem.classList.add(autocompleteClassesEnum.canCreate);
            }
        }
        else if (!canCreate &&
            paperElem.classList.contains(autocompleteClassesEnum.canCreate)) {
            paperElem.classList.remove(autocompleteClassesEnum.canCreate);
        }
    };
    const { current: selectAllConfig } = React.useRef({
        [SELECT_ALL]: `${checkboxSelection?.selectAllLabel || t('selectAll')} `,
    });
    const [value, setValue] = useMixedValue(valueProp, defaultValue);
    const { valueName } = (checkboxSelection ?? {});
    // Add effect to sync the innerValue when form's value prop changes to undefined
    React.useEffect(() => {
        if (valueProp === undefined && defaultValue === undefined) {
            setValue(multiple ? [] : null);
        }
        else if (valueProp === undefined && defaultValue !== undefined) {
            setValue(defaultValue);
        }
    }, [valueProp, setValue, multiple, defaultValue]);
    const getOptionDisabledState = React.useCallback((option) => getOptionDisabled?.(option) ?? option?.disabled ?? false, [getOptionDisabled]);
    const activeOptions = options.filter((option) => !getOptionDisabledState(option));
    const showSelectAll = multiple &&
        activeOptions?.length &&
        (typeof checkboxSelection === 'boolean'
            ? checkboxSelection
            : !!checkboxSelection && (checkboxSelection?.showSelectAll ?? true));
    const getIsAllChecked = (allValue = value) => {
        if (!showSelectAll)
            return false;
        let checkedCount = 0;
        activeOptions.forEach((opt) => {
            const optValue = getCheckboxValue(opt, valueName);
            const itemChecked = allValue?.some((it) => getCheckboxValue(it, valueName) === optValue);
            if (itemChecked) {
                checkedCount++;
            }
        });
        return !!checkedCount && checkedCount === activeOptions.length;
    };
    const isAllChecked = getIsAllChecked();
    const mergedOptions = (showSelectAll ? [selectAllConfig, ...options] : options);
    const valueConfig = (() => {
        if (!showSelectAll)
            return {};
        return !isAllChecked
            ? {
                value: (value ?? []),
            }
            : {
                value: (checkboxSelection?.expandAllTags
                    ? [...mergedOptions.slice(1), mergedOptions[0]]
                    : [mergedOptions[0]]),
            };
    })();
    const { sx: paperSx } = componentsProps.paper ?? {};
    const mergedComponentsProps = {
        ...componentsProps,
        paper: {
            ...componentsProps.paper,
            ref: useForkRef(paperRef, componentsProps?.paper?.ref),
            sx: [
                (theme) => getStyle(theme).paperStyle,
                ...(Array.isArray(paperSx) ? paperSx : [paperSx]),
            ],
            className: clsx(optionClasses.paper, optionClasses[size]),
        },
        popupIndicator: {
            ...componentsProps.popupIndicator,
            'aria-hidden': true,
        },
        clearIndicator: {
            component: Box,
            type: undefined,
            'aria-label': undefined,
            title: undefined,
            role: 'none',
            ...componentsProps.popupIndicator,
        },
    };
    const getEnhancedOptionLabel = (option) => option?._inputValue ||
        option?.[SELECT_ALL] ||
        (option === null ? '' : getOptionLabel(option));
    // Smart default implementation for isOptionEqualToValue
    // Handles creatable options, predefined options, and mixed comparisons
    const createSmartIsOptionEqualToValue = () => (option, value) => {
        // Handle null/undefined cases
        if (option == null || value == null)
            return option === value;
        // Reference equality (fastest path)
        if (option === value)
            return true;
        // Handle creatable options comparison
        if (option._inputValue && value._inputValue) {
            return option._inputValue === value._inputValue;
        }
        // Handle mixed creatable vs string (from user controlled values)
        if (option._inputValue && typeof value === 'string') {
            return option._inputValue === value;
        }
        if (typeof option === 'string' && value._inputValue) {
            return option === value._inputValue;
        }
        // Handle mixed case: one is creatable object, one is regular object
        if (option._inputValue || value._inputValue) {
            return false; // Different types, cannot be equal
        }
        // For predefined options, use stricter comparison
        // Check for common unique identifiers first
        if (option.id !== undefined && value.id !== undefined) {
            return option.id === value.id;
        }
        // If no unique identifiers, fall back to robust comparison
        return isEqualOptions(option, value);
    };
    const handleFilterOptions = (opts, params) => {
        // If user provided custom filterOptions, use it first
        // istanbul ignore if
        if (filterOptions) {
            const userFiltered = filterOptions(opts, params);
            // CRITICAL FIX: Always create a new array to avoid mutating user's returned array
            const finalFiltered = [...userFiltered];
            // Only apply creatable logic if needed
            // istanbul ignore if
            if (creatable) {
                const { inputValue } = params;
                const isExisting = finalFiltered.some((option) => inputValue === getEnhancedOptionLabel(option));
                if (inputValue !== '' && !isExisting) {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const createOption = {
                        _inputValue: inputValue,
                    };
                    finalFiltered.push(createOption);
                    setTimeout(() => {
                        resetCreatablePaperStyle(true);
                    }, 0);
                }
                else if (canCreateRef.current) {
                    resetCreatablePaperStyle(false);
                }
            }
            return finalFiltered;
        }
        // Default filtering logic when no custom filterOptions provided
        const labelCounts = opts.reduce((acc, option) => {
            const label = getEnhancedOptionLabel(option);
            acc[label] = (acc[label] || 0) + 1;
            return acc;
        }, {});
        const filter = createFilterOptions({
            stringify: (option) => {
                const baseString = option[optionFilterName]?.toString() ??
                    getEnhancedOptionLabel(option);
                const label = getEnhancedOptionLabel(option);
                if (labelCounts[label] > 1 && showDuplicateLabels) {
                    const uniqueFields = [
                        option.value,
                        option.id,
                        option.category,
                        option.description,
                        option.type,
                    ]
                        .filter((field) => field != null && field !== '')
                        .join(' ');
                    return `${baseString} ${uniqueFields}`;
                }
                return baseString;
            },
        });
        let filtered = filter(opts, params);
        if (!showDuplicateLabels) {
            const seenLabels = new Set();
            filtered = filtered.filter((option) => {
                const label = getEnhancedOptionLabel(option);
                if (seenLabels.has(label)) {
                    return false;
                }
                seenLabels.add(label);
                return true;
            });
        }
        if (creatable) {
            const { inputValue } = params;
            const isExisting = opts.some((option) => inputValue === getEnhancedOptionLabel(option));
            if (inputValue !== '' && !isExisting) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                const createOption = {
                    _inputValue: inputValue,
                };
                filtered.push(createOption);
                setTimeout(() => {
                    resetCreatablePaperStyle(true);
                }, 0);
            }
            else if (canCreateRef.current) {
                resetCreatablePaperStyle(false);
            }
        }
        return filtered;
    };
    const handleChange = (event, newValue, reason, details) => {
        // Enhanced state synchronization for complex combinations
        // Keep internal state as original objects for consistent comparison
        let internalValue = newValue;
        // Prepare user-facing value by transforming creatable options to expected format
        let userValue = newValue;
        // Enhanced handling for creatable + multiple + checkboxSelection combination
        if (creatable) {
            if (Array.isArray(newValue)) {
                userValue = newValue.map((opt) => {
                    const transformedValue = getOptionValue(opt);
                    // For checkboxSelection, ensure the transformed value maintains the expected structure
                    if (checkboxSelection && typeof opt === 'object' && opt !== null && !opt._inputValue) {
                        return opt; // Keep original object structure for non-creatable options
                    }
                    return transformedValue;
                });
            }
            else {
                userValue = getOptionValue(newValue);
            }
        }
        else if (showSelectAll) {
            const expandAllTags = checkboxSelection?.expandAllTags;
            const currentItemIsAll = details?.option === selectAllConfig;
            const isSelectAllItem = reason !== 'removeOption' && currentItemIsAll;
            let valueWithoutAll;
            if (expandAllTags) {
                valueWithoutAll = isSelectAllItem
                    ? activeOptions
                    : newValue.filter((item) => item !== selectAllConfig);
            }
            else if (isSelectAllItem) {
                valueWithoutAll = activeOptions;
            }
            else {
                valueWithoutAll = newValue.find((val) => val[SELECT_ALL])
                    ? activeOptions.filter((option) => !newValue.find((val) => option === val))
                    : newValue;
            }
            if (reason === 'removeOption' && currentItemIsAll) {
                valueWithoutAll = [];
            }
            internalValue = valueWithoutAll;
            userValue = valueWithoutAll;
        }
        // Only update internal state in uncontrolled mode
        // In controlled mode, the parent component manages the state
        if (valueProp === undefined) {
            setValue(internalValue);
        }
        if (onChange) {
            // Redefine target to allow value to be read.
            // This allows seamless integration with the most popular form libraries.
            // Clone the event to not override `target` of the original event.
            const { nativeEvent } = event;
            const clonedEvent = new nativeEvent.constructor(nativeEvent.type, nativeEvent);
            Object.defineProperty(clonedEvent, 'target', {
                writable: true,
                value: { value: userValue },
            });
            onChange(clonedEvent, userValue, reason, details);
        }
    };
    const renderTags = (val, getTagProps) => {
        const _renderTags = (options) => options.map((option, index) => {
            const label = getEnhancedOptionLabel(option);
            const isSelectAllItem = option === selectAllConfig;
            // Generate unique key for tags
            const getTagKey = (opt, idx) => generateUniqueKey(opt, idx, 'autocomplete-tag');
            // For duplicate labels, show distinguishing information in tag
            const getTagLabel = (opt) => {
                if (isSelectAllItem)
                    return `${label}(${activeOptions.length})`;
                // Check if this label has duplicates in selected values
                const duplicateCount = val.filter((v) => getEnhancedOptionLabel(v) === label).length;
                if (duplicateCount > 1 && showDuplicateLabels) {
                    // Add distinguishing info for duplicate labels in tags
                    const distinguisher = opt?.category || opt?.type || opt?.value || opt?.id;
                    return distinguisher && distinguisher !== label
                        ? `${label} (${distinguisher})`
                        : label;
                }
                return label;
            };
            return (React.createElement(Tag, { "aria-hidden": true, label: getTagLabel(option), ...getTagProps({ index }), key: getTagKey(option, index) }));
        });
        if (checkboxSelection?.expandAllTags &&
            val.find((option) => option === selectAllConfig)) {
            return _renderTags(mergedOptions.filter((option) => option !== selectAllConfig));
        }
        return _renderTags(val);
    };
    const renderInput = (params) => (React.createElement(React.Fragment, null,
        React.createElement(TextField, { ...params, inputProps: {
                ...ariaProps,
                ...params.inputProps,
                ...inputProps,
                // (partially) fix the behavior on MacOS with VO, which reads "completion selected" with aria-controls
                'aria-controls': undefined,
                'aria-description': multiple && value?.length
                    ? t('selectedCount', { count: value.length })
                    : undefined,
            }, placeholder: !value?.length ? placeholder : '', inputRef: inputRef, error: error, autoFocus: autoFocus, sx: [(theme) => getStyle(theme, { size }).textFieldStyle], size: size }),
        helperText ? (React.createElement(FormHelperText, { error: error, role: "alert", sx: [(theme) => getStyle(theme).formHelperTextStyle] },
            error && React.createElement(IconError, { "aria-hidden": true }),
            React.createElement(Box, { component: "span", sx: { ml: '4px' } }, helperText))) : null));
    const defaultRenderGroup = (params) => (React.createElement("li", { key: params.key },
        React.createElement("div", { className: autocompleteClasses.groupLabel }, params.group),
        React.createElement(Box, { "aria-label": params.group, component: "ul", className: autocompleteClasses.groupUl, sx: {
                padding: 0,
            } }, params.children)));
    const renderGroup = renderGroupProp ?? defaultRenderGroup;
    const renderOption = ({ multiline }) => {
        // Use a counter to track option rendering order for unique keys
        let optionIndex = 0;
        return function renderOption(renderProps, option, { selected }) {
            const currentIndex = optionIndex++;
            const _inputValue = option?._inputValue;
            const cls = creatable && _inputValue
                ? {
                    className: clsx(autocompleteClassesEnum.addOption, renderProps.className),
                }
                : {
                    className: clsx(optionClasses.withCheckbox, renderProps.className),
                };
            const label = _inputValue
                ? getCreateLabel(_inputValue)
                : getEnhancedOptionLabel(option);
            const isSelectAllItem = !!option?.[SELECT_ALL];
            // Calculate states using current value for accuracy
            const currentIsAllChecked = getIsAllChecked(value);
            const indeterminate = !currentIsAllChecked && value?.length > 0;
            const isOptionDisabled = getOptionDisabledState(option);
            // FIXED: Calculate the correct selected state for custom renderOption
            // When checkboxSelection is enabled and valueConfig manipulates the internal state,
            // we need to correct the selected parameter passed to custom renderOption
            let correctedSelected = selected;
            // Always apply correction logic when using custom renderOption with checkboxSelection
            if (userRenderOption && checkboxSelection) {
                if (isSelectAllItem) {
                    // For Select All item, use the calculated all-checked state
                    correctedSelected = currentIsAllChecked;
                }
                else {
                    // For regular items: if all are selected OR this specific item is in value
                    const optValue = getCheckboxValue(option, valueName);
                    const isInValue = value?.some((it) => getCheckboxValue(it, valueName) === optValue) ?? false;
                    // If all items are checked, this item should be checked regardless of current value state
                    // This handles async state updates and valueConfig manipulation
                    correctedSelected = currentIsAllChecked || isInValue;
                }
            }
            // Calculate final checked state for default rendering
            let checked;
            if (checkboxSelection) {
                // For checkbox selection mode, use complex logic including all-checked state
                const baseSelected = userRenderOption ? correctedSelected : selected;
                checked = isOptionDisabled ? baseSelected : (currentIsAllChecked || baseSelected);
            }
            else {
                // For normal mode (single/multiple without checkbox), use simple selected state
                checked = isOptionDisabled ? false : selected;
            }
            // Generate unique key for React rendering with proper index
            const getUniqueKey = (opt, idx) => generateUniqueKey(opt, idx, 'autocomplete-option');
            // If user provided custom renderOption, call it with corrected selected state and indeterminate
            if (userRenderOption) {
                return userRenderOption(renderProps, option, {
                    selected: correctedSelected,
                    indeterminate: isSelectAllItem ? indeterminate : false
                });
            }
            return (React.createElement("li", { ...renderProps, ...cls, key: getUniqueKey(option, currentIndex), role: "option", "aria-selected": checked },
                React.createElement(Box, { flex: "1 1 0%", display: "flex", minWidth: 0 },
                    !!checkboxSelection && (React.createElement(Checkbox, { checked: checked, indeterminate: isSelectAllItem && indeterminate, inputProps: { tabIndex: -1 }, sx: { mr: '8px' }, disabled: !isSelectAllItem && getOptionDisabledState(option) })),
                    React.createElement(Box, { flex: "0 1 auto", minWidth: 0 }, multiline ? label : React.createElement(OverflowTooltip, null, label))),
                !checkboxSelection && checked && (React.createElement(IconChecked, { className: autocompleteClassesEnum.checkedIcon }))));
        };
    };
    // eslint-disable-next-line no-nested-ternary
    let computedValue;
    if (valueProp !== undefined) {
        if (multiple || checkboxSelection) {
            if (Array.isArray(valueProp)) {
                computedValue = valueProp;
            }
            else if (valueProp) {
                computedValue = [valueProp];
            }
            else {
                computedValue = [];
            }
        }
        else {
            computedValue = valueProp;
        }
    }
    else {
        computedValue = value ?? ((checkboxSelection ? true : multiple) ? [] : null);
    }
    return (React.createElement(MuiAutocomplete, { className: clsx(className, autocompleteClassesEnum.root, {
            [autocompleteClassesEnum.readOnly]: readOnly,
        }), autoHighlight: true, options: mergedOptions, multiple: (checkboxSelection ? true : multiple), disabled: disabled, readOnly: readOnly, disableClearable: !allowClear, clearIcon: React.createElement(ClearButton, { ...clearButtonProps }), disableCloseOnSelect: multiple, freeSolo: (creatable || freeSolo), clearOnBlur: creatable ? true : !freeSolo && !multiple, getLimitTagsText: getLimitTagsText, getOptionLabel: getEnhancedOptionLabel, filterOptions: handleFilterOptions, isOptionEqualToValue: rest.isOptionEqualToValue ?? createSmartIsOptionEqualToValue(), componentsProps: mergedComponentsProps, popupIcon: React.createElement(IconChevronSmall, { className: autocompleteClassesEnum.suffixIcon, size: 16 }), loadingText: React.createElement("span", { role: "alert" }, loadingText), noOptionsText: React.createElement("span", { role: "alert" }, noOptionsText), onChange: handleChange, value: computedValue, renderInput: renderInput, renderGroup: renderGroup, renderOption: renderOption({
            multiline: longOptionsDisplay === 'multiline',
        }), renderTags: renderTags, getOptionDisabled: getOptionDisabledState, ref: (ins) => {
            if (typeof ref === 'function') {
                ref(ins);
            }
            else if (ref) {
                // eslint-disable-next-line no-param-reassign
                ref.current = ins;
            }
            containerRef.current = ins;
            if (ins) {
                setContainerWidth(ins.clientWidth);
            }
        }, ...valueConfig, ...rest, slotProps: {
            ...rest.slotProps,
            popper: {
                style: {
                    width: longOptionsDisplay === 'multiline' ||
                        longOptionsDisplay === 'trim'
                        ? containerWidth
                        : undefined,
                    minWidth: containerWidth,
                    maxWidth: 'calc(100% - 32px)',
                },
                placement: 'bottom-start',
                ...rest.slotProps?.popper,
            },
        }, onOpen: handleOpen, sx: [
            (theme) => getStyle(theme, { size }).style,
            ...(Array.isArray(sx) ? sx : [sx]),
        ] }));
}
const Autocomplete = React.forwardRef(InternalAutocomplete);
export default Autocomplete;
